<template>
  <div :class="props.class">
    <DatePicker
      v-model="_selectedDate"
      @update:modelValue="$emit('change', $event)"
      :minDate="dateIsBeforeMinDate ? _selectedDate : minDate"
      :maxDate="dateIsAfterMaxDate ? _selectedDate : maxDate"
      :disabledDates="disabledDates"
      class="!p-0 !border-0 !w-full"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { DatePicker } from "v-calendar";

const emit = defineEmits(["change", "submit"]);

const props = defineProps({
  initialState: { type: [String, Date, Number], default: null },
  selectedDate: { type: [String, Date, Number], default: null },
  minDate: { type: Date, default: null },
  maxDate: { type: Date, default: null },
  required: { type: Boolean, default: true },
  class: { type: null, required: false },
});

const _selectedDate = ref(props.selectedDate ?? props.initialState);

const selectedDateDate = computed(() => new Date(_selectedDate.value));

const dateIsBeforeMinDate = computed(() => {
  if (!props.minDate || !_selectedDate.value) {
    return false;
  }
  return selectedDateDate.value < new Date(props.minDate);
});

const dateIsAfterMaxDate = computed(() => {
  if (!props.maxDate || !_selectedDate.value) {
    return false;
  }
  return selectedDateDate.value > new Date(props.maxDate);
});

const disabledDates = computed(() => {
  if (!_selectedDate.value) {
    return [];
  }

  let dates = [];
  if (dateIsBeforeMinDate.value) {
    let date = new Date(selectedDateDate.value);
    let min = new Date(props.minDate);
    date.setDate(date.getDate() - 1);
    const end = new Date(date);
    date.setDate(date.getDate() + 2);
    const start = new Date(date);
    dates.push(
      {
        start: null,
        end,
      },
      {
        start,
        end: new Date(min.setDate(min.getDate() - 1)),
      }
    );
  }
  if (dateIsAfterMaxDate.value) {
    let date = new Date(selectedDateDate.value);
    let max = new Date(props.maxDate);
    date.setDate(date.getDate() - 1);
    const end = new Date(date);
    date.setDate(date.getDate() + 2);
    const start = new Date(date);
    dates.push(
      {
        start: new Date(max.setDate(max.getDate() + 1)),
        end,
      },
      {
        start,
        end: null,
      }
    );
  }

  return dates;
});

const isDirty = computed(() => {
  return _selectedDate.value?.toString() !== props.initialState?.toString();
});

function reset() {
  _selectedDate.value = props.initialState;
}

function submit() {
  if (props.required && !isDirty.value) {
    return;
  }
  emit("submit", _selectedDate.value);
}

function clear() {
  emit("submit", null);
}

defineExpose({
  isDirty,
  reset,
  submit,
  clear,
});
</script>
